
import React, { useState, useEffect } from 'react';
import { Lock, Star, AlertTriangle, BookOpen, Folder as FolderIcon, Map, ChevronDown, Trophy, Play, FileText, X, ArrowLeft, LayoutGrid, Award, CheckCircle } from 'lucide-react';
import { Folder, QuizRecord } from '../data/types';
import { Modal } from './Common';

interface FolderNode extends Folder {
    children: FolderNode[];
}

// Recursive helper to get stats for a whole folder tree (Subject or Unit)
const getRecursiveStats = (rootFolder: FolderNode, quizHistory: QuizRecord[]) => {
    let totalScore = 0;
    let totalPoints = 0;
    let quizCount = 0;

    const traverse = (node: FolderNode) => {
        const records = quizHistory.filter((r: QuizRecord) => r.folderId === node.id);
        records.forEach(r => {
            totalScore += r.score;
            totalPoints += r.totalPoints;
            quizCount++;
        });
        if (node.children) {
            node.children.forEach(traverse);
        }
    };

    traverse(rootFolder);
    
    const avgScore = totalPoints > 0 ? Math.round((totalScore / totalPoints) * 100) : 0;
    
    let status = 'active';
    if (quizCount === 0) status = 'locked';
    else if (avgScore >= 80) status = 'mastered';
    else if (avgScore < 60) status = 'needs-review';

    return { status, avgScore, quizCount };
};

interface ActionModalProps {
    isOpen: boolean;
    onClose: () => void;
    folder: Folder | null;
    onStartQuiz: () => void;
    onReadLecture: () => void;
}

const ActionModal = ({ isOpen, onClose, folder, onStartQuiz, onReadLecture }: ActionModalProps) => {
    if (!isOpen || !folder) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all scale-100 relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                </button>
                <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-blue-50 text-fudan-blue rounded-full flex items-center justify-center mx-auto mb-4">
                        <FolderIcon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1">{folder.name}</h3>
                    <p className="text-gray-500 mb-8 text-sm">請選擇您要進行的學習活動</p>
                    
                    <div className="space-y-3">
                        <button 
                            onClick={() => { onReadLecture(); onClose(); }}
                            className="w-full flex items-center justify-center px-4 py-3 bg-white border-2 border-fudan-blue text-fudan-blue rounded-xl font-bold hover:bg-blue-50 transition-colors"
                        >
                            <FileText className="w-5 h-5 mr-2" />
                            閱讀講義 (學習)
                        </button>
                        <button 
                            onClick={() => { onStartQuiz(); onClose(); }}
                            className="w-full flex items-center justify-center px-4 py-3 bg-fudan-blue text-white rounded-xl font-bold hover:bg-blue-800 transition-colors shadow-md"
                        >
                            <Play className="w-5 h-5 mr-2" />
                            開始測驗 (驗收)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const LearningMapView = ({ folders, quizHistory, onStartFolderQuiz, onReadLecture, rootId }: { folders: Folder[], quizHistory: QuizRecord[], onStartFolderQuiz: (id: string) => void, onReadLecture: (folderId: string) => void, rootId: string }) => {
    const [activeSubjectId, setActiveSubjectId] = useState<string | null>(null);
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Build the tree structure
    const buildTree = (parentId: string | null): FolderNode[] => {
        return folders
            .filter((f: Folder) => (f.parentId || null) === (parentId || null))
            .map((f: Folder) => ({
                ...f,
                children: buildTree(f.id)
            }));
    };

    // Build tree starting from rootId instead of null
    const rootNodes = buildTree(rootId || 'grade-private');

    // Reset active subject when rootId changes (e.g. changing grade)
    useEffect(() => {
        setActiveSubjectId(null);
    }, [rootId]);

    const activeNode = rootNodes.find((n: any) => n.id === activeSubjectId);

    const handleNodeClick = (folder: Folder) => {
        setSelectedFolder(folder);
        setIsModalOpen(true);
    };

    // Card Colors for Subjects
    const getSubjectColor = (index: number) => {
        const colors = [
            { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-fudan-blue', iconBg: 'bg-blue-100' },
            { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', iconBg: 'bg-orange-100' },
            { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', iconBg: 'bg-green-100' },
            { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', iconBg: 'bg-purple-100' },
        ];
        return colors[index % colors.length];
    };

    const getUnitStyle = (status: string) => {
        switch (status) {
            case 'mastered':
                return { 
                    bg: 'bg-green-50', 
                    border: 'border-green-300', 
                    icon: <CheckCircle className="w-6 h-6 text-green-600" />,
                    text: 'text-green-800' 
                };
            case 'needs-review':
                return { 
                    bg: 'bg-red-50', 
                    border: 'border-red-300', 
                    icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
                    text: 'text-red-800' 
                };
            case 'active':
                return { 
                    bg: 'bg-yellow-50', 
                    border: 'border-yellow-300', 
                    icon: <BookOpen className="w-6 h-6 text-yellow-600" />,
                    text: 'text-yellow-800' 
                };
            default: // locked or new
                return { 
                    bg: 'bg-white', 
                    border: 'border-gray-200', 
                    icon: <Lock className="w-6 h-6 text-gray-400" />,
                    text: 'text-gray-600' 
                };
        }
    };

    return (
        <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
            <ActionModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                folder={selectedFolder}
                onStartQuiz={() => selectedFolder && onStartFolderQuiz(selectedFolder.id)}
                onReadLecture={() => selectedFolder && onReadLecture(selectedFolder.id)}
            />

            <div className="bg-white border-b border-gray-200 px-4 py-4 shadow-sm shrink-0 z-20">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <div className="flex items-center">
                        {activeSubjectId && (
                            <button 
                                onClick={() => setActiveSubjectId(null)}
                                className="mr-3 p-2 hover:bg-gray-100 rounded-full text-gray-600 transition-colors"
                                title="返回科目選單"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <h2 className="text-2xl font-bold text-fudan-blue font-serif flex items-center">
                            {activeSubjectId ? (
                                <>
                                    <FolderIcon className="w-6 h-6 mr-2 text-fudan-blue" />
                                    {activeNode?.name}
                                </>
                            ) : (
                                <>
                                    <Map className="w-6 h-6 mr-2" />
                                    學習地圖
                                </>
                            )}
                        </h2>
                    </div>
                    {!activeSubjectId && (
                        <div className="hidden md:flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            <LayoutGrid className="w-4 h-4 mr-2" />
                            請選擇科目以查看詳細單元
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-auto p-4 md:p-8">
                <div className="max-w-6xl mx-auto min-h-[60vh] flex flex-col">
                     
                     {!activeSubjectId ? (
                         // --- Level 1: Subject Selection (Thumbnail Grid) ---
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
                             {rootNodes.length > 0 ? (
                                 rootNodes.map((subject, idx) => {
                                     const stats = getRecursiveStats(subject, quizHistory);
                                     const colors = getSubjectColor(idx);
                                     
                                     return (
                                         <button
                                             key={subject.id}
                                             onClick={() => setActiveSubjectId(subject.id)}
                                             className={`
                                                 relative flex flex-col items-start text-left p-6 rounded-2xl border-2 transition-all duration-300
                                                 ${colors.bg} ${colors.border} hover:shadow-lg hover:-translate-y-1 hover:border-current
                                                 group
                                             `}
                                         >
                                             <div className={`p-4 rounded-full ${colors.iconBg} ${colors.text} mb-4 group-hover:scale-110 transition-transform`}>
                                                 <FolderIcon className="w-8 h-8" />
                                             </div>
                                             
                                             <h3 className={`text-xl font-bold mb-2 ${colors.text}`}>{subject.name}</h3>
                                             
                                             <div className="w-full mt-auto pt-4 border-t border-gray-200/50">
                                                 <div className="flex justify-between items-center text-sm text-gray-600 mb-1">
                                                     <span>已練習題數</span>
                                                     <span className="font-bold">{stats.quizCount} 題</span>
                                                 </div>
                                                 <div className="flex justify-between items-center text-sm text-gray-600">
                                                     <span>平均得分</span>
                                                     <span className={`font-bold ${stats.avgScore >= 80 ? 'text-green-600' : stats.avgScore < 60 && stats.quizCount > 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                                         {stats.avgScore} 分
                                                     </span>
                                                 </div>
                                             </div>
                                             
                                             {stats.avgScore >= 80 && stats.quizCount > 5 && (
                                                 <div className="absolute top-4 right-4">
                                                     <Award className="w-6 h-6 text-yellow-500 drop-shadow-sm" />
                                                 </div>
                                             )}
                                         </button>
                                     );
                                 })
                             ) : (
                                 <div className="col-span-full text-center py-20 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
                                     <FolderIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                     <p>此年級尚無科目資料，請至「題庫資源管理」新增。</p>
                                 </div>
                             )}
                         </div>
                     ) : (
                         // --- Level 2: Topic Grid (Replaces Tree View) ---
                         <div className="flex flex-col animate-fade-in">
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {activeNode && activeNode.children && activeNode.children.length > 0 ? (
                                    activeNode.children.map((child: FolderNode) => {
                                        const stats = getRecursiveStats(child, quizHistory);
                                        const style = getUnitStyle(stats.status);
                                        
                                        return (
                                            <button 
                                                key={child.id}
                                                onClick={() => handleNodeClick(child)}
                                                className={`
                                                    flex items-center p-4 rounded-xl border-2 transition-all hover:shadow-md hover:-translate-y-0.5 text-left
                                                    ${style.bg} ${style.border} hover:border-fudan-blue
                                                `}
                                            >
                                                <div className="mr-4 p-2 bg-white rounded-full shadow-sm">
                                                    {style.icon}
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className={`font-bold text-lg mb-1 ${style.text}`}>{child.name}</h4>
                                                    <div className="flex items-center text-xs text-gray-500 space-x-3">
                                                        <span>練習: {stats.quizCount}</span>
                                                        {stats.quizCount > 0 && (
                                                            <span>平均: {stats.avgScore}分</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {stats.status === 'mastered' && (
                                                    <Trophy className="w-5 h-5 text-yellow-500" />
                                                )}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="col-span-full text-center py-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                                        <FolderIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                        <p>此科目尚無子分類</p>
                                        <p className="text-xs mt-2">請聯繫管理員建立章節結構</p>
                                    </div>
                                )}
                             </div>

                             <div className="mt-8 pt-6 pb-4 flex flex-wrap justify-center gap-4 text-xs md:text-sm text-gray-500 w-full border-t border-gray-200">
                                <div className="flex items-center"><Lock className="w-4 h-4 mr-1 text-gray-400" /> 未測驗</div>
                                <div className="flex items-center"><AlertTriangle className="w-4 h-4 mr-1 text-red-500" /> 需加強 (&lt;60)</div>
                                <div className="flex items-center"><BookOpen className="w-4 h-4 mr-1 text-yellow-600" /> 學習中 (60-79)</div>
                                <div className="flex items-center"><CheckCircle className="w-4 h-4 mr-1 text-green-600" /> 已精通 (≥80)</div>
                             </div>
                         </div>
                     )}
                </div>
            </div>
        </div>
    );
};
